'use client';

import { useActionState, useCallback, useEffect, useMemo, useRef } from 'react';
import { toDiscountPriceItems } from '@/utils/discount-price-items';
import { formatNightsRateLabel } from '@/utils/format-nights-rate-label';
import { getRateDiscountLines } from '@/utils/get-rate-discount-lines';
import { isValidDateParam } from '@/utils/parse-safe-date';
import { Listing, Rate } from '@/utils/platform-api/__generated__/graphql';
import {
  getRateBaseAmount,
  getRateBookingAmount,
  getRateBookingFeesTotal,
  getRateStayFeesAmount,
  getRateTotalBeforeTaxes,
  hasRateStayFees,
} from '@/utils/rate-totals';
import { sum } from 'lodash';
import { ClientBrandConfig } from 'types/brand-config';

import { PriceBreakdown } from '../price-breakdown';
import { PriceBreakdownItem } from '../price-breakdown-popover';
import CancellationBanner from './banner/cancellation-banner';
import AvailabilityInputs from './inputs/availability-inputs';
import PriceRange from './inputs/price-range';
import ReserveBtn from './inputs/reserve-btn';
import { InqIdFormBridge } from './inq-id-form-bridge';
import clickAction from './utils/click-action';

interface AvailabilityFormProps {
  listing: Listing;
  brand: ClientBrandConfig;
  searchParams?: {
    arrive?: string;
    depart?: string;
    guests?: string;
    flex_weeks?: string;
    flex_days?: string;
  };
  rate?: Rate;
}

const AvailabilityForm = ({
  listing,
  brand,
  searchParams,
  rate,
}: AvailabilityFormProps) => {
  const formId = 'availability-widget-form';
  const hasDates = Boolean(
    isValidDateParam(searchParams?.arrive) &&
    isValidDateParam(searchParams?.depart)
  );
  const hasStayFees = rate ? hasRateStayFees(rate) : false;
  const bookingAmount =
    rate && hasStayFees ? getRateBookingAmount(rate) : undefined;
  const stayFees =
    rate && hasStayFees
      ? {
          amount: getRateStayFeesAmount(rate),
        }
      : undefined;
  const prices = rate
    ? createPrices(brand, rate, searchParams?.arrive, searchParams?.depart)
    : [];
  const hasPrices =
    sum(prices.map((fee) => Math.abs(fee.price))) > 0 ||
    (bookingAmount ?? 0) > 0 ||
    (stayFees?.amount ?? 0) > 0;
  const totalBeforeTaxes = rate ? getRateTotalBeforeTaxes(rate) : 0;

  // Use useActionState to access form state and errors from server action
  const [formState, formAction] = useActionState(clickAction, null);

  // Track the searchParams that existed when the error was set
  const errorSearchParamsRef = useRef<typeof searchParams>(null);
  // Track the previous error message to detect new errors
  const prevErrorRef = useRef<string | undefined>(undefined);

  // Function to clear error state (called when dates are cleared)
  const clearError = useCallback(() => {
    errorSearchParamsRef.current = null;
    prevErrorRef.current = undefined;
  }, []);

  // Update error snapshot when a new error occurs (side effect)
  useEffect(() => {
    const currentError = formState?.error;
    const prevError = prevErrorRef.current;

    if (currentError && currentError !== prevError) {
      // New error occurred - capture the searchParams snapshot
      errorSearchParamsRef.current = searchParams;
      prevErrorRef.current = currentError;
    } else if (!currentError) {
      // No error - clear the snapshot
      errorSearchParamsRef.current = null;
      prevErrorRef.current = undefined;
    }
  }, [formState?.error, searchParams]);

  // Determine the error to display (pure computation - no side effects)
  const displayError = useMemo(() => {
    const currentError = formState?.error;

    // If there's no error, don't show anything
    if (!currentError) {
      return undefined;
    }

    // Get the error snapshot
    const errorSnapshot = errorSearchParamsRef.current;

    // If no snapshot exists yet, show the error (will be captured in useEffect)
    if (!errorSnapshot) {
      return currentError;
    }

    // Compare current searchParams to the error snapshot
    const hasChanged =
      errorSnapshot?.arrive !== searchParams?.arrive ||
      errorSnapshot?.depart !== searchParams?.depart ||
      errorSnapshot?.guests !== searchParams?.guests;

    // If searchParams changed since error, don't show the error
    return hasChanged ? undefined : currentError;
  }, [formState?.error, searchParams]);

  return (
    <form
      id={formId}
      action={formAction}
      className="mt-4 flex flex-col items-center gap-4"
    >
      {/* Date Picker */}
      <div className="mt-4 w-full">
        <input hidden type="hidden" name="listing_id" value={listing.id} />
        <InqIdFormBridge />
        <AvailabilityInputs
          searchParams={searchParams}
          error={displayError}
          onClearDates={clearError}
          brand={brand}
          listing={listing}
        />
      </div>
      {listing &&
        !hasDates &&
        listing.rate_summary &&
        listing.rate_summary.min_total_nt &&
        listing.rate_summary.max_total_nt && (
          <div className="my-4">
            <PriceRange
              min_total_nt={listing.rate_summary.min_total_nt}
              max_total_nt={listing.rate_summary.max_total_nt}
            />
          </div>
        )}
      {hasDates && hasPrices && (
        <div className="w-full animate-fade-in">
          <PriceBreakdown
            thinBorder
            prices={prices}
            bookingAmount={bookingAmount}
            stayFees={stayFees}
            price={totalBeforeTaxes}
            priceIsBeforeTaxes
            checkIn={searchParams?.arrive}
            checkOut={searchParams?.depart}
          />
        </div>
      )}
      {hasDates &&
        listing.cancellation_policy &&
        listing.cancellation_policy.fully_refundable &&
        listing.cancellation_policy.grace_period > 0 && (
          <CancellationBanner
            brand={brand}
            gracePeriod={listing.cancellation_policy.grace_period}
            nightsBeforeCheckIn={
              listing.cancellation_policy.nights_before_check_in
            }
            arriveDate={searchParams!.arrive!}
          />
        )}
      <ReserveBtn
        hasDates={hasDates}
        listing={listing}
        brand={brand}
        arriveDate={searchParams?.arrive}
      />
    </form>
  );
};

export default AvailabilityForm;

const createPrices = (
  brand: ClientBrandConfig,
  rate: Rate,
  checkIn?: string,
  checkOut?: string
): PriceBreakdownItem[] => {
  const baseAmount = getRateBaseAmount(rate);
  const nightlyRate = rate.nights > 0 ? baseAmount / rate.nights : 0;

  const items: PriceBreakdownItem[] = [
    {
      label: formatNightsRateLabel(rate.nights, nightlyRate),
      price: baseAmount,
    },
  ];

  items.push(
    ...toDiscountPriceItems(getRateDiscountLines(rate, checkIn, checkOut))
  );

  const bookingFeeDetails = createDetails(brand, rate);
  const bookingFeesTotal = getRateBookingFeesTotal(rate);

  if (bookingFeesTotal > 0 || bookingFeeDetails.length > 0) {
    items.push({
      label: 'Fees',
      price: bookingFeesTotal,
      details: bookingFeeDetails,
      detailsPresentation: 'modal',
    });
  }

  return items;
};

enum FeeDetails {
  Departure_Cleaning = 'Departure Cleaning',
  Daily_Cleaning = 'Daily Cleaning',
  Damage_Waiver = 'Damage Waiver',
  Resort_Fee = 'Resort Fee',
  Service_Charge = 'Service Charge',
  Booking_Fee = 'Guest Assurance Fee',
}

const createDetails = (brand: ClientBrandConfig, rate?: Rate) => {
  const details: { label: string; price: number; tooltip?: string }[] = [];
  if (rate?.fee_details?.clean) {
    details.push({
      label: FeeDetails.Departure_Cleaning,
      price: rate.fee_details.clean,
    });
  }
  if (rate?.fee_details?.cleaning_daily) {
    details.push({
      label: FeeDetails.Daily_Cleaning,
      price: rate.fee_details.cleaning_daily,
    });
  }
  if (rate?.fee_details?.damage) {
    details.push({
      label: FeeDetails.Damage_Waiver,
      price: rate.fee_details.damage,
    });
  }
  const stayCollected = rate?.fee_details?.stay_collected_total ?? 0;
  const rentCollectedResort = (rate?.fee_details?.resort ?? 0) - stayCollected;
  if (rentCollectedResort > 0) {
    details.push({
      label: FeeDetails.Resort_Fee,
      price: rentCollectedResort,
    });
  }
  if (rate?.fee_details?.service) {
    details.push({
      label: FeeDetails.Service_Charge,
      price: rate.fee_details.service,
    });
  }
  if (rate?.fee_details?.booking) {
    details.push({
      label: brand.bookingFee?.title || FeeDetails.Booking_Fee,
      price: rate.fee_details.booking,
      tooltip: brand.bookingFee?.description,
    });
  }
  return details;
};
